import { useEffect, useState } from "react";
import { useAlert } from "../../../Hooks/useAlert";
import { useLanguage } from "../../../context/LanguageContext";
import expensesLanguage from "../../../language/expensesLanguage";
import { otherExpenseAPI, purchaseMilkAPI } from "../../../services/api";
import Alert from "../../common/Alert";
import ConfirmDialog from "../../common/ConfirmDialog";
import BackButton from "./BackButton";

const TODAY = () => new Date().toISOString().split("T")[0];

function ExpensesPage() {
  const { language } = useLanguage();
  const text = expensesLanguage[language];
  const { showAlert, showConfirm, alertConfig, confirmConfig } = useAlert();

  const [activeTab, setActiveTab] = useState("purchase");

  const [purchaseId, setPurchaseId] = useState(0);
  const [purchaseName, setPurchaseName] = useState("");
  const [purchaseMilkType, setPurchaseMilkType] = useState(0);
  const [purchaseQty, setPurchaseQty] = useState("");
  const [purchaseRate, setPurchaseRate] = useState("");
  const [purchaseDate, setPurchaseDate] = useState(TODAY());
  const [purchaseEntries, setPurchaseEntries] = useState([]);
  const [purchaseLoading, setPurchaseLoading] = useState(false);

  const [expenseId, setExpenseId] = useState(0);
  const [productType, setProductType] = useState("");
  const [productAmount, setProductAmount] = useState("");
  const [expenseDate, setExpenseDate] = useState(TODAY());
  const [expenseEntries, setExpenseEntries] = useState([]);
  const [expenseLoading, setExpenseLoading] = useState(false);

  useEffect(() => {
    loadPurchaseEntries();
    loadExpenseEntries();
  }, []);

  const loadPurchaseEntries = async () => {
    try {
      const response = await purchaseMilkAPI.getLast5DaysEntries();
      if (response.success) setPurchaseEntries(response.data || []);
    } catch (error) {
      console.error("Error loading purchase entries:", error);
    }
  };

  const loadExpenseEntries = async () => {
    try {
      const response = await otherExpenseAPI.getExpenses();
      if (response.success) setExpenseEntries(response.data || []);
    } catch (error) {
      console.error("Error loading expense entries:", error);
    }
  };

  const resetPurchaseForm = () => {
    setPurchaseId(0);
    setPurchaseName("");
    setPurchaseMilkType(0);
    setPurchaseQty("");
    setPurchaseRate("");
    setPurchaseDate(TODAY());
  };

  const handleSavePurchase = async () => {
    if (!purchaseName.trim() || !purchaseQty || !purchaseRate) {
      await showAlert({ type: "warning", title: text.validationError, message: text.purchaseMilkValidation });
      return;
    }
    setPurchaseLoading(true);
    try {
      const data = {
        purchaseEntryId: purchaseId,
        name: purchaseName.trim(),
        milkType: purchaseMilkType,
        purchaseQtyLiters: parseFloat(purchaseQty),
        purchaseRate: parseFloat(purchaseRate),
        entryDate: purchaseDate,
      };
      const response = await purchaseMilkAPI.savePurchaseEntry(data);
      if (response.success) {
        await showAlert({ type: "success", title: text.success, message: purchaseId === 0 ? text.savedSuccess : text.updatedSuccess });
        resetPurchaseForm();
        await loadPurchaseEntries();
      } else {
        await showAlert({ type: "error", title: text.error, message: text.saveFailed });
      }
    } catch (error) {
      console.error("Error saving purchase:", error);
      await showAlert({ type: "error", title: text.error, message: text.saveFailed });
    } finally {
      setPurchaseLoading(false);
    }
  };

  const handleEditPurchase = (entry) => {
    setPurchaseId(entry.purchaseEntryId);
    setPurchaseName(entry.name || "");
    setPurchaseMilkType(entry.milkType);
    setPurchaseQty(entry.purchaseQtyLiters.toString());
    setPurchaseRate(entry.purchaseRate.toString());
    setPurchaseDate(new Date(entry.entryDate).toISOString().split("T")[0]);
  };

  const handleDeletePurchase = async (entry) => {
    const confirmed = await showConfirm({
      type: "error",
      title: text.deleteConfirmTitle,
      message: text.deleteConfirmMsg,
      confirmText: text.delete,
      cancelText: text.deleteCancel,
    });
    if (!confirmed) return;
    try {
      const response = await purchaseMilkAPI.deletePurchaseEntry(entry.purchaseEntryId);
      if (response.success) {
        await showAlert({ type: "success", title: text.success, message: text.deletedSuccess });
        await loadPurchaseEntries();
      } else {
        await showAlert({ type: "error", title: text.error, message: text.deleteFailed });
      }
    } catch (error) {
      console.error("Error deleting purchase:", error);
      await showAlert({ type: "error", title: text.error, message: text.deleteFailed });
    }
  };

  const resetExpenseForm = () => {
    setExpenseId(0);
    setProductType("");
    setProductAmount("");
    setExpenseDate(TODAY());
  };

  const handleSaveExpense = async () => {
    if (!productType.trim() || !productAmount) {
      await showAlert({ type: "warning", title: text.validationError, message: text.expensesValidation });
      return;
    }
    setExpenseLoading(true);
    try {
      const data = {
        expenseId,
        productType: productType.trim(),
        amount: parseFloat(productAmount),
        entryDate: expenseDate,
      };
      const response = await otherExpenseAPI.saveExpense(data);
      if (response.success) {
        await showAlert({ type: "success", title: text.success, message: expenseId === 0 ? text.savedSuccess : text.updatedSuccess });
        resetExpenseForm();
        await loadExpenseEntries();
      } else {
        await showAlert({ type: "error", title: text.error, message: text.saveFailed });
      }
    } catch (error) {
      console.error("Error saving expense:", error);
      await showAlert({ type: "error", title: text.error, message: text.saveFailed });
    } finally {
      setExpenseLoading(false);
    }
  };

  const handleEditExpense = (entry) => {
    setExpenseId(entry.expenseId);
    setProductType(entry.productType);
    setProductAmount(entry.amount.toString());
    setExpenseDate(new Date(entry.entryDate).toISOString().split("T")[0]);
  };

  const handleDeleteExpense = async (entry) => {
    const confirmed = await showConfirm({
      type: "error",
      title: text.deleteConfirmTitle,
      message: text.deleteConfirmMsg,
      confirmText: text.delete,
      cancelText: text.deleteCancel,
    });
    if (!confirmed) return;
    try {
      const response = await otherExpenseAPI.deleteExpense(entry.expenseId);
      if (response.success) {
        await showAlert({ type: "success", title: text.success, message: text.deletedSuccess });
        await loadExpenseEntries();
      } else {
        await showAlert({ type: "error", title: text.error, message: text.deleteFailed });
      }
    } catch (error) {
      console.error("Error deleting expense:", error);
      await showAlert({ type: "error", title: text.error, message: text.deleteFailed });
    }
  };

  const inputClass = "settings-form-input";
  const labelClass = "settings-form-label";

  return (
    <div className="settings-page">
      <header className="settings-page-header">
        <BackButton to="/settings" section="salesExpenses" />
        <h1 className="settings-page-title">Expenses Master</h1>
      </header>

      <main className="settings-page-content">
        <div className="settings-form-card">
          <div className="settings-form-body">
            <div className="settings-tabs">
              <button
                type="button"
                onClick={() => setActiveTab("purchase")}
                className={`settings-tab ${activeTab === "purchase" ? "active" : ""}`}
              >
                <i className="fas fa-truck" />
                <span>{text.tabPurchaseMilk}</span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("other")}
                className={`settings-tab ${activeTab === "other" ? "active" : ""}`}
              >
                <i className="fas fa-receipt" />
                <span>{text.tabOtherExpenses}</span>
              </button>
            </div>

            {activeTab === "purchase" && (
              <div className="settings-tab-content">
                <div className="settings-form-group">
                  <label className={labelClass}>{text.name}</label>
                  <input type="text" value={purchaseName} onChange={(e) => setPurchaseName(e.target.value)} className={inputClass} placeholder={text.name} />
                </div>
                <div className="settings-form-group">
                  <label className={labelClass}>{text.milkType}</label>
                  <select value={purchaseMilkType} onChange={(e) => setPurchaseMilkType(parseInt(e.target.value))} className={`${inputClass} settings-form-select`}>
                    <option value={0}>{text.milkTypeCow}</option>
                    <option value={1}>{text.milkTypeBuffalo}</option>
                  </select>
                </div>
                <div className="settings-form-group">
                  <label className={labelClass}>{text.quantity}</label>
                  <input type="number" step="0.1" value={purchaseQty} onChange={(e) => setPurchaseQty(e.target.value)} className={inputClass} />
                </div>
                <div className="settings-form-group">
                  <label className={labelClass}>{text.rate}</label>
                  <input type="number" step="0.01" value={purchaseRate} onChange={(e) => setPurchaseRate(e.target.value)} className={inputClass} />
                </div>
                <div className="settings-form-group">
                  <label className={labelClass}>{text.date}</label>
                  <input type="date" value={purchaseDate} max={TODAY()} onChange={(e) => setPurchaseDate(e.target.value)} className={inputClass} />
                </div>
                <div className="settings-form-actions">
                  <button type="button" disabled={purchaseLoading} onClick={handleSavePurchase} className="settings-form-btn">
                    <i className="fas fa-save" />
                    <span>{purchaseLoading ? text.saving : purchaseId === 0 ? text.save : text.update}</span>
                  </button>
                  {purchaseId !== 0 && (
                    <button type="button" onClick={resetPurchaseForm} className="settings-form-btn settings-form-btn-secondary">
                      <i className="fas fa-times" />
                      <span>{text.cancel}</span>
                    </button>
                  )}
                </div>

                {purchaseEntries.length > 0 && (
                  <div className="settings-table-wrapper">
                    <h3 className="settings-table-title">{text.recentEntries}</h3>
                    <table className="settings-table">
                      <thead>
                        <tr>
                          <th className="settings-table-th settings-table-th-center">{text.colActions}</th>
                          <th className="settings-table-th">{text.colDate}</th>
                          <th className="settings-table-th">{text.colName}</th>
                          <th className="settings-table-th">{text.colType}</th>
                          <th className="settings-table-th settings-table-th-right">{text.colQty}</th>
                          <th className="settings-table-th settings-table-th-right">{text.colRate}</th>
                          <th className="settings-table-th settings-table-th-right">{text.colTotal}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {purchaseEntries.map((entry, index) => (
                          <tr key={entry.purchaseEntryId} className={`settings-table-tr ${index % 2 === 1 ? "settings-table-tr-zebra" : ""}`}>
                            <td className="settings-table-td settings-table-td-center">
                              <div className="settings-table-actions">
                                <button type="button" onClick={() => handleEditPurchase(entry)} className="settings-action-btn settings-action-btn-edit">
                                  <i className="fas fa-pen" />
                                </button>
                                <button type="button" onClick={() => handleDeletePurchase(entry)} className="settings-action-btn settings-action-btn-delete">
                                  <i className="fas fa-trash-alt" />
                                </button>
                              </div>
                            </td>
                            <td className="settings-table-td">{new Date(entry.entryDate).toLocaleDateString("en-IN")}</td>
                            <td className="settings-table-td">{entry.name}</td>
                            <td className="settings-table-td">{entry.milkType === 0 ? text.milkTypeCow : text.milkTypeBuffalo}</td>
                            <td className="settings-table-td settings-table-td-right">{entry.purchaseQtyLiters.toFixed(2)}</td>
                            <td className="settings-table-td settings-table-td-right">{entry.purchaseRate.toFixed(2)}</td>
                            <td className="settings-table-td settings-table-td-right settings-table-td-bold">{(entry.purchaseQtyLiters * entry.purchaseRate).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeTab === "other" && (
              <div className="settings-tab-content">
                <div className="settings-form-group">
                  <label className={labelClass}>{text.productType}</label>
                  <input type="text" value={productType} onChange={(e) => setProductType(e.target.value)} className={inputClass} placeholder={text.productType} />
                </div>
                <div className="settings-form-group">
                  <label className={labelClass}>{text.productAmount}</label>
                  <input type="number" step="0.01" value={productAmount} onChange={(e) => setProductAmount(e.target.value)} className={inputClass} />
                </div>
                <div className="settings-form-group">
                  <label className={labelClass}>{text.date}</label>
                  <input type="date" value={expenseDate} max={TODAY()} onChange={(e) => setExpenseDate(e.target.value)} className={inputClass} />
                </div>
                <div className="settings-form-actions">
                  <button type="button" disabled={expenseLoading} onClick={handleSaveExpense} className="settings-form-btn">
                    <i className="fas fa-save" />
                    <span>{expenseLoading ? text.saving : expenseId === 0 ? text.save : text.update}</span>
                  </button>
                  {expenseId !== 0 && (
                    <button type="button" onClick={resetExpenseForm} className="settings-form-btn settings-form-btn-secondary">
                      <i className="fas fa-times" />
                      <span>{text.cancel}</span>
                    </button>
                  )}
                </div>

                {expenseEntries.length > 0 && (
                  <div className="settings-table-wrapper">
                    <h3 className="settings-table-title">{text.recentEntries}</h3>
                    <table className="settings-table">
                      <thead>
                        <tr>
                          <th className="settings-table-th settings-table-th-center">{text.colActions}</th>
                          <th className="settings-table-th">{text.colDate}</th>
                          <th className="settings-table-th">{text.colProduct}</th>
                          <th className="settings-table-th settings-table-th-right">{text.colAmount}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {expenseEntries.map((entry, index) => (
                          <tr key={entry.expenseId} className={`settings-table-tr ${index % 2 === 1 ? "settings-table-tr-zebra" : ""}`}>
                            <td className="settings-table-td settings-table-td-center">
                              <div className="settings-table-actions">
                                <button type="button" onClick={() => handleEditExpense(entry)} className="settings-action-btn settings-action-btn-edit">
                                  <i className="fas fa-pen" />
                                </button>
                                <button type="button" onClick={() => handleDeleteExpense(entry)} className="settings-action-btn settings-action-btn-delete">
                                  <i className="fas fa-trash-alt" />
                                </button>
                              </div>
                            </td>
                            <td className="settings-table-td">{new Date(entry.entryDate).toLocaleDateString("en-IN")}</td>
                            <td className="settings-table-td">{entry.productType}</td>
                            <td className="settings-table-td settings-table-td-right settings-table-td-bold">₹{entry.amount.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {alertConfig && <Alert {...alertConfig} />}
      {confirmConfig && <ConfirmDialog {...confirmConfig} />}
    </div>
  );
}

export default ExpensesPage;
